/* eslint-disable @typescript-eslint/explicit-function-return-type */
import * as Oazapfts from "oazapfts/lib/runtime";
import * as QS from "oazapfts/lib/runtime/query";
import { authStatusWrapper, wrapper } from "./base";
import {
	Activity,
	ActivityAdd,
	ActivityType,
	Assignment,
	AssignmentAdd,
	AssignmentForEditing,
	Children,
	CreateUserRequest,
	GetGrade,
	Group,
	GroupAccuracy,
	GroupForEditing,
	GroupResult,
	IndividualResult,
	NewComment,
	Realm,
	RegisterAdmin,
	RegisterGroup,
	RegisterUsersResponse,
	RegisteredUserList,
	RoleType,
	School,
	SchoolResponse,
	SortOrder,
	UUID,
	UpdateGrade,
	User,
	UserChildEmailForEditing,
	UserListBasic,
	UserOptionalPropsAndPasswordForEditing,
	UserOptionalPropsForEditing,
	UserReport,
	UserResponseWithActivities,
	UserWithProgress,
	assignmentResultCSV,
	outMetrics,
	outSchoolMetrics,
	studentsGradeDistributionMetrics,
} from "./types";

/**
 * Get the list of all schools (public, no auth required — used for Google SSO profile completion)
 */
export const getPublicSchools = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: { schoolId: string; schoolName: string }[];
		  }
		| {
				status: 500;
		  }
	>("/public/schools", { ...opts });

/**
 * JIT-provision a teacher who authenticated via Google SSO but has no local account yet.
 */
export const provisionGoogleTeacher = (
	body: { school_action: "create" | "join"; school_name?: string; school_id?: string },
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 201;
				data: { userId: string; schoolId: string };
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 409;
		  }
	>(
		"/auth/google/provision",
		wrapper.json({ ...opts, method: "POST", body }),
	);

/**
 * Register a new admin account for a new school
 */
export const registerAdminNewSchool = (
	registerAdmin: RegisterAdmin,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 201;
				data: {
					school?: School;
				};
		  }
		| {
				status: 400;
		  }
		| {
				status: 409;
		  }
	>(
		"/register",
		wrapper.json({
			...opts,
			method: "POST",
			body: registerAdmin,
		}),
	);

/**
 * Get the data for a school.
 *
 * | Role          | Access |
 * | ------------- | ------ |
 * | Administrator | ✅     |
 * | Teacher       | ❌     |
 * | Student       | ❌     |
 * | Parent        | ❌     |
 *
 */
export const getSchool = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: SchoolResponse;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
		| {
				status: 404;
		  }
	>("/school", {
		...opts,
	});

/**
 * Upload a list of users
 */
export const registerUsers = (
	body: CreateUserRequest[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 201;
				data: RegisterUsersResponse;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
		| {
				status: 404;
		}
		| {
			status: 409;
		}
	>(
		"/school/register-users",
		wrapper.json({
			...opts,
			method: "POST",
			body,
		}),
	);

/**
 * Get the list of groups which the user can see
 */
export const getGroups = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Group[] | null;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>("/groups/", {
		...opts,
	});

/**
 * Create a new group
 */
export const addGroup = (
	registerGroup: RegisterGroup,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		"/groups/",
		wrapper.json({
			...opts,
			method: "POST",
			body: registerGroup,
		}),
	);

/**
 * Get a specific group
 */
export const getGroup = (groupId: UUID, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Group;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>(`/groups/${groupId}`, {
		...opts,
	});

/**
 * Update a group's attributes (but not the users)
 */
export const updateGroup = (
	groupId: UUID,
	groupForEditing: GroupForEditing,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/groups/${groupId}`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: groupForEditing,
		}),
	);

/**
 * Delete a group
 */
export const deleteGroup = (groupId: UUID, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchText(`/groups/${groupId}`, {
		...opts,
		method: "DELETE",
	});

/**
 * Get a specific group's users
 */
export const getGroupUsers = (
	groupId: UUID,
	{
		role,
		firstName,
		lastName,
		group,
		start,
		count,
		sortBy,
		sortOrder,
	}: {
		role?: RoleType;
		firstName?: string;
		lastName?: string;
		group?: string;
		start?: number;
		count?: number;
		sortBy?: "firstName" | "lastName";
		sortOrder?: SortOrder;
	} = {},
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: User[] | null;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>(
		`/groups/${groupId}/users${QS.query(
			QS.form({
				role,
				firstName,
				lastName,
				group,
				start,
				count,
				sortBy,
				sortOrder,
			}),
		)}`,
		{
			...opts,
		},
	);

/**
 * Add more students to the group
 */
export const addUsersToGroup = (
	groupId: UUID,
	body: UUID[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/groups/${groupId}/users`,
		wrapper.json({
			...opts,
			method: "PUT",
			body,
		}),
	);

/**
 * Delete users from the group
 */
export const deleteUsersFromGroup = (
	groupId: UUID,
	body: UUID[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/groups/${groupId}/users`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body,
		}),
	);

/**
 * Get the list of assignments which the user can see
 */
export const getAssignments = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Assignment[] | null;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>("/assignments/", {
		...opts,
	});

/**
 * Create a new Assignment
 */
export const addAssignment = (
	assignmentAdd: AssignmentAdd,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		"/assignments/",
		wrapper.json({
			...opts,
			method: "POST",
			body: assignmentAdd,
		}),
	);

/**
 * Get a specific assignment
 */
export const getAssignment = (
	assignmentId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Assignment;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>(`/assignments/${assignmentId}`, {
		...opts,
	});

/**
 * Update an assignments's attributes
 */
export const updateAssignment = (
	assignmentId: UUID,
	assignmentForEditing: AssignmentForEditing,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/assignments/${assignmentId}`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: assignmentForEditing,
		}),
	);

/**
 * Delete an assignment
 */
export const deleteAssignment = (
	assignmentId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(`/assignments/${assignmentId}`, {
		...opts,
		method: "DELETE",
	});

/**
 * End an assignment
 */
export const endAssignment = (
	assignmentId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(`/assignments/${assignmentId}/finish`, {
		...opts,
		method: "POST",
	});

/**
 * Get the list of assignments which are assigned to a group
 */
export const getGroupAssignments = (
	groupId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Assignment[] | null;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>(`/assignments/groups/${groupId}`, {
		...opts,
	});

/**
 * Add activities to an assignment
 */
export const addActivitiesToAssignment = (
	assignmentId: UUID,
	body: ActivityAdd[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/assignments/${assignmentId}/activities`,
		wrapper.json({
			...opts,
			method: "PUT",
			body,
		}),
	);

/**
 * Delete activities from an assignment
 */
export const deleteActivitiesFromAssignment = (
	assignmentId: UUID,
	body: ActivityAdd[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/assignments/${assignmentId}/activities`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body,
		}),
	);

/**
 * Get an assignments report
 */
export const getAssignmentReport = (
	assignmentId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: UserReport[] | null;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>(`/report/assignments/${assignmentId}`, {
		...opts,
	});

/**
 * Get an assignments leaderboard
 */
export const getAssignmentLeaderboard = (
	assignmentId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: UserListBasic;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
	>(`/report/assignments/${assignmentId}/leaderboard`, {
		...opts,
	});

/**
 * Get a user's details
 */
export const getUserDetails = (userId: UUID, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: UserResponseWithActivities;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
		| {
				status: 404;
		  }
	>(`/user/${userId}`, {
		...opts,
	});

/**
 * Update a user's details
 */
export const updateUser = (
	userId: UUID,
	userOptionalPropsAndPasswordForEditing: UserOptionalPropsAndPasswordForEditing,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/user/${userId}`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: userOptionalPropsAndPasswordForEditing,
		}),
	);

/**
 * Update a user's email
 */
export const updateUserEmail = (
	userId: UUID,
	userOptionalPropsForEditing: UserOptionalPropsAndPasswordForEditing,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/user/${userId}/update-email/`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: userOptionalPropsForEditing,
		}),
	);

/**
 * Update a user's name
 */
export const updateUserName = (
	userId: UUID,
	userOptionalPropsForEditing: UserOptionalPropsForEditing,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchText(
		`/user/${userId}/update-name`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: userOptionalPropsForEditing,
		}),
	);

/**
 * Query the users list
 */
export const getUsers = (
	{
		role,
		firstName,
		lastName,
		group,
		start,
		count,
		sortBy,
		sortOrder,
	}: {
		role?: RoleType;
		firstName?: string;
		lastName?: string;
		group?: string;
		start?: number;
		count?: number;
		sortBy?: "firstName" | "lastName";
		sortOrder?: SortOrder;
	} = {},
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: User[] | null;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
		| {
				status: 404;
		  }
	>(
		`/users${QS.query(
			QS.form({
				role,
				firstName,
				lastName,
				group,
				start,
				count,
				sortBy,
				sortOrder,
			}),
		)}`,
		{
			...opts,
		},
	);

/**
 * Delete user accounts
 */
export const deleteUsers = (body: UUID[], opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchText(
		"/users",
		wrapper.json({
			...opts,
			method: "PATCH",
			body,
		}),
	);

/**
 * Add Administrators
 */
export const addAdmin = (body: UUID[], opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchText(
		"/administrator",
		wrapper.json({
			...opts,
			method: "PUT",
			body,
		}),
	);

/**
 * Remove Administrator
 */
export const removeAdmin = (body: UUID[], opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchText(
		"/administrator",
		wrapper.json({
			...opts,
			method: "PATCH",
			body,
		}),
	);

export interface GetActivitiesParams {
	name?: string;
	realm?: Realm[];
	activityType?: ActivityType[];
	count?: number;
	page?: number;
}

/**
 * Query the activities list
 */
export const getActivities = (
	{ name, realm, activityType, count, page }: GetActivitiesParams = {},
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Activity[] | null;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
		  }
		| {
				status: 404;
		  }
	>(
		`/activities${QS.query(
			QS.form({
				name,
				realm,
				activityType,
				count,
				page,
			}),
		)}`,
		{
			...opts,
		},
	);

/**
 * Sign in to Science Island
 */
export const signin = (
	body: {
		email?: string;
		password?: string;
	},
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: {
					user?: User;
				};
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
		| {
				status: 403;
				data: {
					action: string;
				};
		  }
		| {
				status: 404;
				data: {
					action: string;
					email: string;
					firstName: string;
					lastName: string;
					keycloakId: string;
				};
		  }
	>(
		"/auth/signin/",
		wrapper.json({
			...opts,
			method: "POST",
			body,
		}),
	);

/**
 * Check to see if the user is signed in.
 */
export const authStatus = (opts?: Oazapfts.RequestOpts) =>
	authStatusWrapper.fetchJson<
		| {
				status: 200;
				data: {
					role: RoleType;
				};
		  }
		| {
				status: 401;
		  }
	>("/auth/status", {
		...opts,
	});

/**
 * Sign out of Science Island
 */
export const signout = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchText("/auth/signout", {
		...opts,
		method: "POST",
	});

/**
 * Get the requesting user's data
 */
export const getMyUserData = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: UserWithProgress;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>("/me", {
		...opts,
	});

export const getIndividualResult = (
	userId: UUID,
	from?: string,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: IndividualResult;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(
		`/result/${userId}${QS.query(
			QS.form({
				from,
			}),
		)}`,
		{
			...opts,
		},
	);

export const getRegisteredUsers = (
	from?: string,
	to?: string,
	count?: string,
	page?: string,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: RegisteredUserList;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(
		`/registered-users${QS.query(
			QS.form({
				from,
				to,
				count,
				page,
			}),
		)}`,
		{
			...opts,
		},
	);

export const getMultiStudentResults = (
	userIds: UUID[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: IndividualResult[];
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(
		`/result/`,
		wrapper.json({
			...opts,
			method: "POST",
			body: {
				userIdList: userIds,
			},
		}),
	);

export const getGroupResult = (groupId: UUID, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: GroupResult;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(`/result/groups/${groupId}`, {
		...opts,
	});

export const getMultiGroupResults = (
	groupIds: UUID[],
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: GroupResult[];
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(
		`/result/groups/`,
		wrapper.json({
			...opts,
			method: "POST",
			body: {
				groupIdList: groupIds,
			},
		}),
	);

/**
 * Update a parent's child
 */
export const addChildToParent = (
	userId: UUID,
	userChildEmailForEditing: UserChildEmailForEditing,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: UserWithProgress;
		  }
		| {
				status: 400;
		  }
	>(
		`/parent/${userId}/add-child`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: userChildEmailForEditing,
		}),
	);

/**
 * Get individual metrics
 */
export const getIndividualMetrics = (
	userId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: outMetrics;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(`/metrics/Individual/${userId}`, {
		...opts,
	});
/**
 * Get School metrics
 */
export const getSchoolMetrics = (schoolId: UUID, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: outSchoolMetrics;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(`/metrics/School/${schoolId}`, {
		...opts,
	});

/**
 * Get School metrics
 */
export const getStudentGradeDistributionMetrics = (
	schoolId: UUID,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: studentsGradeDistributionMetrics;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(`/metrics/viewSchool/${schoolId}`, {
		...opts,
	});

export const getGroupMetrics = (groupId: UUID, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: outSchoolMetrics;
		  }
		| {
				status: 400;
		  }
		| {
				status: 401;
		  }
	>(`/metrics/Group/${groupId}`, {
		...opts,
	});

/**
 * Update student's evaluation grade
 */
export const updateEvaluationGrade = (
	updateGrade: UpdateGrade,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 204;
		  }
		| {
				status: 400;
		  }
		| {
				status: 504;
		  }
	>(
		`/result/grades`,
		wrapper.json({
			...opts,
			method: "PATCH",
			body: updateGrade,
		}),
	);

/**
 * Add a new comment
 */
export const addComment = (
	newComment: NewComment,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 201;
		  }
		| {
				status: 400;
		  }
		| {
				status: 504;
		  }
	>(
		`/result/comments`,
		wrapper.json({
			...opts,
			method: "POST",
			body: newComment,
		}),
	);

/**
 * Send email with attachments
 */
export const sendResult = (result: FormData, opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 201;
		  }
		| {
				status: 400;
				error: {
					Error: string;
				};
		  }
	>(`/email/send-result`, {
		...opts,
		method: "POST",
		body: result,
	});

/**
 * Create student's evaluation grade
 */
export const createEvaluationGrade = (
	updateGrade: UpdateGrade,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 201;
		  }
		| {
				status: 400;
		  }
	>(
		`/result/grades`,
		wrapper.json({
			...opts,
			method: "POST",
			body: updateGrade,
		}),
	);

/**
 * Get Student's grades
 */
export const getGrades = (
	studentId: string,
	gradeType: string,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: GetGrade[];
		  }
		| {
				status: 204;
		  }
		| {
				status: 400;
		  }
	>(`/result/grades/${gradeType}/${studentId}`, {
		...opts,
	});

/**
 * Get Parent's children
 */
export const GetChildren = (opts?: Oazapfts.RequestOpts) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: Children[];
		  }
		| {
				status: 204;
		  }
		| {
				status: 400;
		  }
	>(`/parent/get-children`, {
		...opts,
	});

/**
 * Get Assignment result to generate CSV
 */
export const GetAssignmentResultCSV = (
	assignmentID: string,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: assignmentResultCSV[];
		  }
		| {
				status: 204;
		  }
		| {
				status: 400;
		  }
	>(`/assignments/result/${assignmentID}`, {
		...opts,
	});

/**
 * Get Group Accuracy
 */
export const GetGroupAccuracy = (
	schoolID: string,
	opts?: Oazapfts.RequestOpts,
) =>
	wrapper.fetchJson<
		| {
				status: 200;
				data: GroupAccuracy[];
		  }
		| {
				status: 204;
		  }
		| {
				status: 400;
		  }
	>(`/metrics/GroupAccuracy/${schoolID}`, {
		...opts,
	});
